import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { isEqual, omit } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { combineLatest, Observable, withLatestFrom } from 'rxjs';
import { Patient, PatientContacts, PatientDetailsFormValues } from 'src/app/core/models';
import { EnvService } from 'src/app/core/services';
import { FetchPatientContacts, UpdatePatientDetails } from 'src/app/core/store/contacts/contacts.actions';
import { ContactsState } from 'src/app/core/store/contacts/contacts.state';
import { PatientState } from 'src/app/core/store/patient/patient.state';
import { ApiStoreData, CcsSentryError } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';
import { duplicatePhoneValidator, phoneValidator, validateAllFormFields } from 'src/app/core/utils/validators';

@Component({
  selector: 'app-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.scss'],
})
export class PatientComponent implements OnInit {
  @Select(PatientState.patient) patient$!: Observable<Patient | null>;
  @Select(PatientState.updatePatientState) updatePatientState$!: Observable<ApiStoreData<null>>;
  @Select(ContactsState.patientContactsState) patientContactsState$!: Observable<ApiStoreData<PatientContacts | null>>;
  @Select(ContactsState.isUpdatingPatientDetails) isUpdatingPatientDetails$!: Observable<boolean>;
  @Select(ContactsState.updatePatientDetailsError) updatePatientDetailsError$!: Observable<CcsSentryError[] | null>;
  @Select(ContactsState.isUpdatingPatientDetailsFinished) isUpdatingPatientDetailsFinished$!: Observable<boolean>;

  patientDetailsForm = new FormGroup(
    {
      firstName: new FormControl(null, [Validators.required]),
      lastName: new FormControl(null, [Validators.required]),
      primaryEmail: new FormControl(null),
      primaryPhoneNumber: new FormControl(null, [Validators.required, phoneValidator()]),
      isPrimaryPhoneMobile: new FormControl(false),
      secondaryPhoneNumber: new FormControl(null, [phoneValidator()]),
      isSecondaryPhoneMobile: new FormControl(false),
      consentedToMarketing: new FormControl(false),
      refusedGenericMedications: new FormControl(false),
    },
    [duplicatePhoneValidator('primaryPhoneNumber', 'secondaryPhoneNumber')]
  );

  medicalProfileUrl: string = this.envService.medicalProfile.url;
  isEditMode: boolean = false;
  initialValues: PatientDetailsFormValues | null = null;

  asFormControl = asFormControl;

  constructor(private store: Store, private envService: EnvService, private messageService: MessageService) {}

  ngOnInit() {
    this.patientDetailsForm.get('primaryEmail')?.disable();

    combineLatest([this.patient$, this.patientContactsState$, this.isUpdatingPatientDetails$]).subscribe(
      ([patientData, patientContactsState, isUpdatingPatientDetails]) => {
        if (!patientData || !patientContactsState.data || isUpdatingPatientDetails) return;

        this.initialValues = {
          firstName: patientData.firstName,
          lastName: patientData.lastName,
          primaryEmail: patientContactsState.data.primaryEmail || null,
          primaryPhoneNumber: patientContactsState.data.primaryPhone?.phoneNumber || null,
          isPrimaryPhoneMobile: patientContactsState.data.primaryPhone?.isMobile || false,
          secondaryPhoneNumber: patientContactsState.data.secondaryPhone?.phoneNumber || null,
          isSecondaryPhoneMobile: patientContactsState.data.secondaryPhone?.isMobile || false,
          consentedToMarketing: patientData.consentedToMarketing,
          refusedGenericMedications: patientData.refusedGenericMedications,
        };

        this.patientDetailsForm.setValue(this.initialValues);
      }
    );

    this.store.dispatch(new FetchPatientContacts());
  }

  switchToEdit() {
    this.isEditMode = true;
  }

  endEdit() {
    this.isEditMode = false;
  }

  onSubmitPatientDetailsForm() {
    if (!this.patientDetailsForm.valid) {
      validateAllFormFields(this.patientDetailsForm);
      return;
    }

    if (isEqual(omit(this.patientDetailsForm.value, 'primaryEmail'), omit(this.initialValues, 'primaryEmail'))) {
      this.endEdit();
      return;
    }

    // Save the form with the store..
    this.store
      .dispatch(new UpdatePatientDetails(this.patientDetailsForm.value))
      .pipe(withLatestFrom(this.isUpdatingPatientDetailsFinished$), withLatestFrom(this.updatePatientDetailsError$))
      .subscribe(([[_, isFinished], errors]) => {
        if (isFinished) {
          if (!errors) {
            this.endEdit();
            this.messageService.clear('profileMessages');
            this.messageService.add({
              key: 'profileMessages',
              severity: 'success',
              detail: 'Patient details updated successfully.',
            });
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
            return;
          }
          window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        }
      });
  }

  onCancelPatientDetailsForm() {
    this.endEdit();
    if (this.initialValues) this.patientDetailsForm.setValue(this.initialValues);
  }

  getErrorMessage(errors: CcsSentryError[] | null): string {
    if (!errors || !errors.length) return '';
    return errors[0].uiMessage || '';
  }
}
